<template lang="pug">
  div.filter-item

    .filter__and
      span(v-show="subqueryIndex !== 0" color="grey") {{ conditionTypeText }}

    .filter__type
      v-select(
        :items="availableFilterFields"
        item-text="name"
        item-value="field"
        :value="selectedCondition"
        @input="updateField"
        return-object
        data-cy="condition-select"
      )
        template(slot="item" slot-scope="{ item }")
          v-layout(:data-cy="getDropdownDataCy(item)")
            v-icon.shrink(size="medium") {{item.icon}}
            div.filter-field__name.grow.pl-1.pr-2 {{item.name}}
            v-icon.shrink(
              size="medium"
              v-if="item.secondaryIcon"
            ) {{ item.secondaryIcon }}
        template(slot="selection" slot-scope="{ item }")
          //- Use the secondary icon in the primary position.
          //- enables showing IDX in that limited size position.
          //- a balance between showing IDX and showing the type
          //- But the time should be more obvious at this point based
          //- on the operands; the IDX icon is fully lost once selected
          v-icon.shrink(size="medium") {{ item.secondaryIcon || item.icon }}
          div.filter-field__name.grow.pl-1.pr-2 {{item.name}}

    component(
      :is="`${selectedComponent}-condition`"
      v-if="selectedCondition"
      :value="selectedCondition"
      :operators="selectedCondition.operators"
      :options="selectedCondition.options"
      @input="updateFilter"
    )

    v-spacer(grow)

    v-layout.delete-condition(row shrink)
        v-flex
          v-btn.filter__action(flat icon @click="$emit('delete', subqueryIndex, index)")
            v-icon(medium color="grey") delete
    v-layout.add-condition-button.mr-1.text-xs-center(column shrink)
      v-flex(v-if="subqueryIndex === conditionsLength - 1" xs12)
        v-btn.and-or-button(
          color="primary"
          outline
          small
          @click="$emit('add-condition', { index, conditionType: AND_CONDITION })"
        )
          | And
      v-flex(v-if="subqueryIndex === conditionsLength - 1" xs12)
        v-btn.and-or-button-second(
          color="primary"
          outline
          small
          @click="$emit('add-condition', { index, conditionType: OR_CONDITION })"
        )
          | Or

</template>

<script>
import MultiselectCondition from './MultiselectCondition';
import ForeignkeyCondition from './ForeignKeyCondition';
import HotnessCondition from './HotnessCondition';
import IntegerCondition from './IntegerCondition';
import DateCondition from './DateCondition';
import TextCondition from './TextCondition';
import { AND_CONDITION, OR_CONDITION } from '../../utilities/filter-query-builders/constants';

export default {
  components: {
    MultiselectCondition,
    ForeignkeyCondition,
    HotnessCondition,
    IntegerCondition,
    DateCondition,
    TextCondition,
  },
  props: {
    condition: {
      type: Object,
      required: true,
    },
    availableFilterFields: {
      type: Array,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    subqueryIndex: {
      type: Number,
      default: 0,
    },
    conditionsLength: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      selectedCondition: null,
      AND_CONDITION,
      OR_CONDITION,
    };
  },
  created() {
    this.selectedCondition = Object.assign({}, this.condition);
    this.loadSelectedConditionOptions();
  },
  computed: {
    /**
     * Left here for backwards compatibility;
     * new, more specific naming is .filterComponent;
     * old naming is .type
     */
    selectedComponent() {
      return this.selectedCondition.filterComponent || this.selectedCondition.type;
    },
    conditionTypeText() {
      return this.selectedCondition.conditionType ? this.selectedCondition.conditionType.toLowerCase() : '';
    },
  },
  methods: {

    /**
     * The selected condition usually comes from the saved conditions, which isn't
     * saved with the options
     *
     * No need to load options if they already exist or there's no loadWith specified.
     */
    loadSelectedConditionOptions() {
      if (this.selectedCondition.options && this.selectedCondition.options.length) {
        this.updateLegacyHotnessValue();
        return;
      }
      if (!this.selectedCondition.loadWith) return;

      const selectedFilterField = this.availableFilterFields.find(field => {
        return field.loadWith === this.selectedCondition.loadWith;
      });

      this.selectedCondition.options = [...selectedFilterField.options];
      this.selectedCondition.operators = selectedFilterField.operators ?
        [...selectedFilterField.operators] : [];
      this.updateLegacyHotnessValue();
    },

    /**
     * Add support for the saved hotness filter when the field is 'hotness'
     * and the value is the hotness name.
     */
    updateLegacyHotnessValue() {
      if (this.selectedCondition.field !== 'hotness_id') return;

      const option = this.selectedCondition.options.find(option => {
        return option.value === this.selectedCondition.value[0];
      });

      if (!option) return;

      this.selectedCondition.field = 'hotness';
      this.selectedCondition.value = [option.text];
    },

    /**
     * Update the field filter
     *
     * Clear the operator & value as we're moving to a different filter field
     * Clear the loadWith and options, since not all filters have those properties
     */
    updateField(newCondition) {
      this.selectedCondition.operator = null;
      this.selectedCondition.value = null;
      this.selectedCondition.loadWith = null;
      this.selectedCondition.options = [];

      this.updateFilter(newCondition);
    },

    /**
     * this.selectedCondition:
     *    icon: "calendar_today",
     *    field: "created_at",
     *    name: "Created at",
     *    operator: "today",
     *    type: date,
     *    value: null
     *
     * Propagate the changes up to the parent component.
     *
     * If the newCondition is of a different type than the previously selected condition,
     * then we clear the operator and value
     *
     * This function is called under two circumstances:
     * 1. The dynamic child component emits a message upward, containing only the
     *    new operator and operand.
     * 2. The field itself has changed, and it calls this function directly with
     *    a full set of details.
     *
     * Given that we can't replace the selectedCondition with the newFilter,
     * we merge the two.
     *
     * @param  {object} newFilter - The new condition formed from the form inputs
     * @return {}
     */
    updateFilter(newFilter) {
      this.selectedCondition = Object.assign(this.selectedCondition, newFilter);
      this.$emit('input', this.index, this.subqueryIndex, this.selectedCondition);
    },

    /**
     * Cypress test method
    */
    getDropdownDataCy(item) {
      return `condition-${this.availableFilterFields.indexOf(item)}`;
    },
  },
};
</script>
